import React, { useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, timeAgo, resolveMediaUrl } from '../../lib/api';
import { useApp } from '../../context/AppContext';
import { Avatar, AvatarImage, AvatarFallback } from '../ui/avatar';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { VerifiedBadge } from '../app/VerifiedBadge';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../ui/dropdown-menu';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from '../ui/drawer';
import { Heart, MessageCircle, Send, MoreHorizontal, Flag, Languages, Dumbbell, Link2, ChevronLeft, ChevronRight, CornerDownRight, Pencil, Trash2, Archive } from 'lucide-react';
import { toast } from 'sonner';

function CommentBlock({ comment, replies, onReply, t }) {
  const navigate = useNavigate();
  const [showReply, setShowReply] = useState(false);
  const [replyText, setReplyText] = useState('');

  return (
    <div className="py-2">
      <div className="flex gap-2.5">
        <Avatar className="w-8 h-8 cursor-pointer shrink-0" onClick={() => navigate(`/profile/${comment.author?.id}`)}>
          <AvatarImage src={resolveMediaUrl(comment.author?.avatar)} />
          <AvatarFallback>{comment.author?.username?.[0]}</AvatarFallback>
        </Avatar>
        <div className="flex-1 min-w-0">
          <p className="text-sm leading-snug">
            <span className="font-semibold me-1.5">{comment.author?.username}</span>
            {comment.author?.verified && <VerifiedBadge size={12} className="inline-block me-1" />}
            <span className="text-foreground/90">{comment.text}</span>
          </p>
          <div className="flex items-center gap-3 mt-1">
            <span className="text-[11px] text-muted-foreground">{timeAgo(comment.created_at)}</span>
            <button onClick={() => setShowReply(!showReply)} className="text-[11px] text-muted-foreground font-semibold hover:text-foreground flex items-center gap-1">
              <CornerDownRight size={10} /> {t('reply')}
            </button>
          </div>
          {showReply && (
            <div className="flex gap-2 mt-2">
              <Input data-testid="comment-reply-input" value={replyText} onChange={(e) => setReplyText(e.target.value)} placeholder={`${t('reply')}...`} className="h-8 text-xs"
                onKeyDown={(e) => { if (e.key === 'Enter' && replyText.trim()) { onReply(replyText, comment.id); setReplyText(''); setShowReply(false); } }} />
              <Button size="sm" variant="secondary" className="h-8 px-3" onClick={() => { if (replyText.trim()) { onReply(replyText, comment.id); setReplyText(''); setShowReply(false); } }}>
                <Send size={13} />
              </Button>
            </div>
          )}
          {replies.map((r) => (
            <div key={r.id} className="flex gap-2 mt-2 ms-4">
              <Avatar className="w-6 h-6 shrink-0"><AvatarImage src={r.author?.avatar} /><AvatarFallback>{r.author?.username?.[0]}</AvatarFallback></Avatar>
              <p className="text-xs leading-snug">
                <span className="font-semibold me-1">{r.author?.username}</span>
                <span className="text-foreground/90">{r.text}</span>
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function PostCard({ post, onDeleted }) {
  const { t, lang, user } = useApp();
  const navigate = useNavigate();
  const [liked, setLiked] = useState(post.liked_by_me);
  const [likeCount, setLikeCount] = useState(post.like_count);
  const [shares, setShares] = useState(post.shares || 0);
  const [commentCount, setCommentCount] = useState(post.comment_count || 0);
  const [commentsOpen, setCommentsOpen] = useState(false);
  const [comments, setComments] = useState([]);
  const [commentText, setCommentText] = useState('');
  const [translated, setTranslated] = useState(null);
  const [translating, setTranslating] = useState(false);
  const [showTranslated, setShowTranslated] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [imgIndex, setImgIndex] = useState(0);
  const [showHeart, setShowHeart] = useState(false);
  const [postText, setPostText] = useState(post.text || '');
  const [editText, setEditText] = useState(post.text || '');
  const [editOpen, setEditOpen] = useState(false);
  const scrollerRef = useRef(null);
  const rafRef = useRef(0);
  const lastTapRef = useRef(0);
  const heartTimerRef = useRef(null);
  const images = post.images || [];
  const videos = post.videos || [];
  const media = [
    ...images.map((src) => ({ type: 'image', src })),
    ...videos.map((src) => ({ type: 'video', src })),
  ];

  const toggleLike = async (forceLike = false) => {
    if (forceLike && liked) {
      // double-tap when already liked: just show animation
      return;
    }
    const nextLiked = forceLike ? true : !liked;
    setLiked(nextLiked);
    setLikeCount((c) => (nextLiked ? c + 1 : c - 1));
    try {
      const res = await api.post(`/posts/${post.id}/like`);
      setLiked(res.data.liked); setLikeCount(res.data.like_count);
    } catch {
      setLiked(liked); setLikeCount(likeCount);
    }
  };

  const popHeart = () => {
    setShowHeart(false);
    clearTimeout(heartTimerRef.current);
    requestAnimationFrame(() => {
      setShowHeart(true);
      heartTimerRef.current = setTimeout(() => setShowHeart(false), 900);
    });
  };

  const handleMediaTap = () => {
    const now = Date.now();
    if (now - lastTapRef.current < 300) {
      popHeart();
      toggleLike(true);
      if (navigator.vibrate) navigator.vibrate(10);
      lastTapRef.current = 0;
    } else {
      lastTapRef.current = now;
    }
  };

  // --- carousel: native scroll-snap; dots synced via rAF-throttled scroll handler ---
  const onCarouselScroll = useCallback(() => {
    if (rafRef.current) return;
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = 0;
      const el = scrollerRef.current;
      if (!el || el.clientWidth === 0) return;
      const idx = Math.round(Math.abs(el.scrollLeft) / el.clientWidth);
      setImgIndex((prev) => {
        const clamped = Math.max(0, Math.min(idx, media.length - 1));
        return prev === clamped ? prev : clamped;
      });
    });
  }, [media.length]);

  const goTo = (idx) => {
    const el = scrollerRef.current;
    if (!el) return;
    const clamped = Math.max(0, Math.min(idx, media.length - 1));
    el.children[clamped]?.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
  };

  const openComments = async () => {
    setCommentsOpen(true);
    try {
      const res = await api.get(`/posts/${post.id}/comments`);
      setComments(res.data);
    } catch {}
  };

  const addComment = async (text, parentId = null) => {
    try {
      const res = await api.post(`/posts/${post.id}/comments`, { text, parent_id: parentId });
      setComments((c) => [...c, res.data]);
      setCommentCount((c) => c + 1);
    } catch { toast.error('Failed'); }
  };

  const doShare = async () => {
    try {
      const res = await api.post(`/posts/${post.id}/share`);
      setShares(res.data.shares);
      navigator.clipboard?.writeText(`${window.location.origin}/?post=${post.id}`).catch(() => {});
      toast.success(t('share') + ' ✓');
      setShareOpen(false);
    } catch { toast.error('Failed'); }
  };

  const doTranslate = async () => {
    if (showTranslated) { setShowTranslated(false); return; }
    if (translated) { setShowTranslated(true); return; }
    setTranslating(true);
    try {
      const res = await api.post('/translate', { text: postText, target_lang: lang });
      setTranslated(res.data.translated_text);
      setShowTranslated(true);
    } catch { toast.error('Translation failed'); }
    setTranslating(false);
  };

  const doReport = async () => {
    try {
      await api.post('/reports', { content_type: 'post', content_id: post.id, reason: 'Inappropriate content' });
      toast.success(t('report') + ' ✓');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const saveEdit = async () => {
    try {
      const response = await api.put(`/posts/${post.id}`, { text: editText });
      setPostText(response.data.text || '');
      setEditOpen(false);
      toast.success(t('saved'));
    } catch (error) {
      toast.error(error.response?.data?.detail || t('connectionError'));
    }
  };

  const archivePost = async () => {
    try {
      await api.post(`/posts/${post.id}/archive`);
      onDeleted?.(post.id);
      toast.success(t('postArchived'));
    } catch (error) {
      toast.error(error.response?.data?.detail || t('connectionError'));
    }
  };

  const deletePost = async () => {
    if (!window.confirm(t('deletePostConfirm'))) return;
    try {
      await api.delete(`/posts/${post.id}`);
      onDeleted?.(post.id);
      toast.success(t('postDeleted'));
    } catch (error) {
      toast.error(error.response?.data?.detail || t('connectionError'));
    }
  };

  const roots = comments.filter((c) => !c.parent_id);
  const repliesOf = (id) => comments.filter((c) => c.parent_id === id);

  return (
    <article className="bg-card sm:rounded-[22px] border-y sm:border border-border/80 fade-up sm:overflow-hidden elev-1" data-testid="post-card">
      {/* Header */}
      <div className="flex items-center gap-3 px-3 py-2.5">
        <Avatar className="w-9 h-9 cursor-pointer" onClick={() => navigate(`/profile/${post.author?.id}`)}>
          <AvatarImage src={resolveMediaUrl(post.author?.avatar)} />
          <AvatarFallback>{post.author?.username?.[0]?.toUpperCase()}</AvatarFallback>
        </Avatar>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1">
            <span className="text-sm font-semibold truncate cursor-pointer" onClick={() => navigate(`/profile/${post.author?.id}`)} data-testid="post-author-username">
              {post.author?.username}
            </span>
            {post.author?.verified && <VerifiedBadge size={14} />}
            <span className="text-xs text-muted-foreground ms-1">· {timeAgo(post.created_at)}</span>
          </div>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button data-testid="post-menu-button" className="p-1.5 rounded-lg hover:bg-secondary text-muted-foreground transition-colors">
              <MoreHorizontal size={18} />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {user?.id === post.user_id ? (
              <>
                <DropdownMenuItem data-testid="post-edit-button" onClick={() => { setEditText(postText); setEditOpen(true); }}>
                  <Pencil size={14} className="me-2" /> {t('editPost')}
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="post-archive-button" onClick={archivePost}>
                  <Archive size={14} className="me-2" /> {t('archivePost')}
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="post-delete-button" onClick={deletePost} className="text-destructive">
                  <Trash2 size={14} className="me-2" /> {t('delete')}
                </DropdownMenuItem>
              </>
            ) : (
              <DropdownMenuItem data-testid="post-report-button" onClick={doReport} className="text-destructive">
                <Flag size={14} className="me-2" /> {t('report')}
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Media carousel */}
      {media.length > 0 && (
        <div className="relative select-none" data-testid="post-media">
          <div
            ref={scrollerRef}
            className="post-carousel aspect-square sm:aspect-[4/5] bg-foreground/10"
            onScroll={onCarouselScroll}
            onClick={(event) => event.target.tagName === 'IMG' && handleMediaTap()}
            data-testid="post-carousel"
          >
            {media.map((item, i) => item.type === 'video' ? (
              <video key={`${item.src}-${i}`} src={resolveMediaUrl(item.src)} className="w-full h-full object-contain bg-black" controls playsInline preload="metadata" />
            ) : (
              <img key={`${item.src}-${i}`} src={resolveMediaUrl(item.src)} alt={`post ${i + 1}`} className="w-full h-full object-cover" loading={i === 0 ? 'eager' : 'lazy'} draggable={false} />
            ))}
          </div>
          {/* double-tap heart pop */}
          {showHeart && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10">
              <Heart size={96} className="text-white heart-pop drop-shadow-2xl" fill="white" />
            </div>
          )}
          {media.length > 1 && (
            <>
              {/* counter pill */}
              <span className="absolute top-3 end-3 text-[11px] bg-black/60 text-white rounded-full px-2 py-0.5 z-10 tabular-nums" data-testid="post-carousel-counter">
                {imgIndex + 1}/{media.length}
              </span>
              {/* desktop arrows */}
              {imgIndex > 0 && (
                <button
                  data-testid="post-carousel-prev"
                  onClick={(e) => { e.stopPropagation(); goTo(imgIndex - 1); }}
                  className="hidden sm:flex absolute start-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/55 hover:bg-black/75 items-center justify-center text-white z-10 transition-colors"
                  aria-label="previous image"
                >
                  <ChevronLeft size={18} className="rtl:rotate-180" />
                </button>
              )}
              {imgIndex < media.length - 1 && (
                <button
                  data-testid="post-carousel-next"
                  onClick={(e) => { e.stopPropagation(); goTo(imgIndex + 1); }}
                  className="hidden sm:flex absolute end-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/55 hover:bg-black/75 items-center justify-center text-white z-10 transition-colors"
                  aria-label="next image"
                >
                  <ChevronRight size={18} className="rtl:rotate-180" />
                </button>
              )}
              {/* dots */}
              <div className="absolute -bottom-0 inset-x-0 translate-y-5 flex justify-center gap-1.5 pointer-events-none" data-testid="post-carousel-dots">
                {media.map((_, i) => (
                  <span key={i} className={`w-1.5 h-1.5 rounded-full transition-colors duration-150 ${i === imgIndex ? 'bg-foreground' : 'bg-muted-foreground/40'}`} />
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {/* Action row */}
      <div className={`flex items-center gap-1 px-2 py-1.5 ${media.length > 1 ? 'mt-4' : 'mt-0.5'}`} data-testid="post-action-row">
        <button data-testid="post-like-button" onClick={() => toggleLike()} aria-label={t('like')}
          className={`p-2 rounded-full hover:bg-primary/10 transition-colors active:scale-[0.92] ${liked ? 'text-destructive' : 'text-foreground'}`}>
          <Heart size={24} fill={liked ? 'currentColor' : 'none'} strokeWidth={1.8} />
        </button>
        <button data-testid="post-comment-button" onClick={openComments} aria-label={t('comment')}
          className="p-2 rounded-full hover:bg-secondary/60 text-foreground transition-colors active:scale-[0.92]">
          <MessageCircle size={24} strokeWidth={1.8} />
        </button>
        <button data-testid="post-share-button" onClick={() => setShareOpen(true)} aria-label={t('share')}
          className="p-2 rounded-full hover:bg-secondary/60 text-foreground transition-colors active:scale-[0.92]">
          <Send size={23} strokeWidth={1.8} />
        </button>
      </div>

      {/* Like count */}
      <div className="px-4">
        <p className="text-sm font-semibold tabular-nums" data-testid="post-like-count">{likeCount} {t('likes')}</p>
      </div>

      {/* Caption */}
      {postText && (
        <div className="px-4 pt-1">
          <p className="text-sm leading-relaxed">
            <span className="font-semibold me-1.5 cursor-pointer" onClick={() => navigate(`/profile/${post.author?.id}`)}>{post.author?.username}</span>
            <span className="whitespace-pre-wrap text-foreground/90">{showTranslated ? translated : postText}</span>
          </p>
          <button data-testid="post-translate-button" onClick={doTranslate} disabled={translating}
            className="text-xs text-muted-foreground hover:text-foreground mt-1 flex items-center gap-1 transition-colors">
            <Languages size={12} />
            {translating ? t('loading') : showTranslated ? t('showOriginal') : t('translate')}
          </button>
        </div>
      )}

      {/* Exercise log */}
      {post.exercise_log?.exercise && (
        <div className="mx-4 mt-2 p-3 rounded-lg bg-secondary/50 border border-border">
          <div className="flex items-center gap-2 text-xs font-semibold mb-1 text-muted-foreground uppercase tracking-wide">
            <Dumbbell size={13} /> {t('exercises')}
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm">
            <span className="font-medium">{post.exercise_log.exercise}</span>
            <span className="text-muted-foreground">{post.exercise_log.sets} × {post.exercise_log.reps}</span>
            {post.exercise_log.weight && post.exercise_log.weight !== '-' && <span className="font-medium">{post.exercise_log.weight}</span>}
          </div>
        </div>
      )}

      {/* View comments link */}
      <div className="px-4 pt-1.5 pb-3">
        {commentCount > 0 && (
          <button data-testid="post-view-comments-button" onClick={openComments} className="text-sm text-muted-foreground hover:text-foreground transition-colors">
            {t('viewAllComments')} ({commentCount})
          </button>
        )}
      </div>

      {/* Comments slide-up drawer */}
      <Drawer open={commentsOpen} onOpenChange={setCommentsOpen}>
        <DrawerContent className="max-h-[80vh]" data-testid="comments-drawer">
          <DrawerHeader className="py-3 border-b border-border">
            <DrawerTitle className="text-center text-sm font-semibold">{t('comments')}</DrawerTitle>
          </DrawerHeader>
          <div className="overflow-y-auto px-4 flex-1 min-h-[200px]" data-testid="post-comments-section">
            {roots.length === 0 && <p className="text-center text-sm text-muted-foreground py-10">{t('addComment')}</p>}
            {roots.map((c) => (
              <CommentBlock key={c.id} comment={c} replies={repliesOf(c.id)} onReply={addComment} t={t} />
            ))}
          </div>
          <div className="flex gap-2 p-3 border-t border-border bg-card">
            <Input data-testid="post-comment-input" value={commentText} onChange={(e) => setCommentText(e.target.value)}
              placeholder={t('addComment')} className="h-10 text-sm rounded-full px-4"
              onKeyDown={(e) => { if (e.key === 'Enter' && commentText.trim()) { addComment(commentText); setCommentText(''); } }} />
            <Button data-testid="post-comment-submit" size="sm" className="h-10 rounded-full px-4" onClick={() => { if (commentText.trim()) { addComment(commentText); setCommentText(''); } }}>
              <Send size={15} />
            </Button>
          </div>
        </DrawerContent>
      </Drawer>

      {/* Share dialog */}
      <Dialog open={shareOpen} onOpenChange={setShareOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>{t('share')}</DialogTitle></DialogHeader>
          <Button data-testid="post-share-confirm-button" onClick={doShare} className="w-full">
            <Link2 size={15} className="me-2" /> {t('share')} · {shares}
          </Button>
        </DialogContent>
      </Dialog>
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{t('editPost')}</DialogTitle></DialogHeader>
          <Textarea data-testid="post-edit-textarea" value={editText} onChange={(event) => setEditText(event.target.value)} maxLength={2000} />
          <Button data-testid="post-edit-save-button" onClick={saveEdit} disabled={!editText.trim() && media.length === 0}>{t('save')}</Button>
        </DialogContent>
      </Dialog>
    </article>
  );
}
